import { assert_type_number, assert_type_string } from '@shared/asserts/utils_asserts';
import { UTILS__crypto } from '@shared/utils/crypto/UTILS__crypto';
import { DTOPerformaceCapture } from '@shared/utils/performance/dto_performance_capture';
import { number_to_hex } from '@shared/utils/utils_colors';
import { format_size } from '@shared/utils/utils_files';
import { format_time_ms, format_timestamp_HH_mm_ss, timestamp_ms } from '@shared/utils/utils_time';
import { ConstantsLogger } from './constants_logger';
import { ELoggerNamespaceShared } from './e_logger_namespace_shared';
import { ILogMessageParams } from './i_log_message_params';
import { LogEntry } from './log_entry';

export class BuilderLogEntry {
  /*
   * STATIC FIELDS
   */

  static COLORS_MAP: Map<string, string> = new Map();

  static {
    this.COLORS_MAP.set(ELoggerNamespaceShared.CLIENT_TO_SERVER, '#DFC184');
    this.COLORS_MAP.set(ELoggerNamespaceShared.SERVER_TO_CLIENT, '#73ACE7');
    this.COLORS_MAP.set(ELoggerNamespaceShared.SERVICE_FS, '#1abc9c');
    this.COLORS_MAP.set(ELoggerNamespaceShared.PERFORMANCE_CAPTURE, ConstantsLogger.COLOR_NOT_IMPORTANT);
    this.COLORS_MAP.set(ELoggerNamespaceShared.MUTEX, ConstantsLogger.COLOR_NOT_IMPORTANT);
    this.COLORS_MAP.set(ELoggerNamespaceShared.GLOBAL, '');
    this.COLORS_MAP.set(ELoggerNamespaceShared.WARNING, ConstantsLogger.COLOR_WARNING);
  }

  /*
   * STATIC METHODS
   */

  static get_color(namespace: string) {
    if (this.COLORS_MAP.has(namespace)) return this.COLORS_MAP.get_or_assert(namespace);

    if (!namespace) debugger;

    let hash = UTILS__crypto.djb2.from_string(namespace);

    return ConstantsLogger.COLORS[Math.abs(hash) % ConstantsLogger.COLORS.length];
  }

  static build(message: string, params: ILogMessageParams): LogEntry {
    let out = new LogEntry();
    out.message = message;
    out.params = params;

    this.add_column_log_time(out);

    this.add_column_diff_time(out);

    this.add_column_bytes(out);

    this.add_column_log_entry_id(out);

    this.add_column_message(out);

    return out;
  }

  /*
   * COLUMN LOG TIME
   */

  static add_column_log_time(entry: LogEntry): void {
    entry.column_log_time = entry.message ? this.get_log_time() : '';

    entry.column_log_time = entry.column_log_time.pad(ConstantsLogger.LOGGER_COLUMN_LOG_TIME_CHARS);

    assert_type_string(entry.params.namespace);
    let color = this.get_color(entry.params.namespace);

    if (color) this.colorize_column_log_time(color, entry);
  }

  static get_log_time() {
    return format_timestamp_HH_mm_ss(timestamp_ms());
  }

  static colorize_column_log_time(color: string, entry: LogEntry) {
    //do nothing here
  }

  /*
   * COLUMN DIFF TIME
   */

  static add_column_diff_time(entry: LogEntry): void {
    let { params } = entry;

    assert_type_string(entry.params.namespace);

    let color = this.get_color(entry.params.namespace);

    let column_diff_time = '';

    let performance_capture = params.performance_capture || DTOPerformaceCapture.CREATE_STUB();

    if (!performance_capture.is_stub()) {
      assert_type_number(performance_capture.duration);
      assert_type_number(performance_capture.performance_reference);

      column_diff_time = `in ${format_time_ms(performance_capture.duration)}`;

      if (performance_capture.duration >= performance_capture.performance_reference) {
        color = ConstantsLogger.COLOR_ERROR;
      }
    } else if (params.log_time_diff_show) {
      assert_type_number(params.log_time_diff);

      column_diff_time = params.log_time_diff > 0 ? `+${format_time_ms(params.log_time_diff)}` : '';

      if (params.log_time_diff >= ConstantsLogger.LOG_DIFF_REFERENCE) {
        color = ConstantsLogger.COLOR_ERROR;
      }
    }

    column_diff_time = column_diff_time.pad(ConstantsLogger.LOGGER_COLUMN_TIME_DIFF_CHARS);

    entry.column_diff_time = column_diff_time;

    if (color) this.colorize_column_time_diff(color, entry);
  }

  static colorize_column_time_diff(color: string, entry: LogEntry) {
    //do nothing here
  }

  /*
   * COLUMN BYTES
   */

  static add_column_bytes(entry: LogEntry): void {
    assert_type_number(entry.params.bytes);
    assert_type_number(entry.params.bytes_reference);

    entry.column_bytes = entry.params.bytes == 0 ? '' : format_size(entry.params.bytes, true);

    entry.column_bytes = entry.column_bytes.pad(ConstantsLogger.LOGGER_COLUMN_BYTES_CHARS);

    let color = '';

    if (entry.params.bytes == 0) {
      color = ConstantsLogger.COLOR_NOT_IMPORTANT;
    } else if (entry.params.bytes >= entry.params.bytes_reference) {
      color = ConstantsLogger.COLOR_ERROR;
    }

    if (color) this.colorize_column_bytes(color, entry);
  }

  static colorize_column_bytes(color: string, entry: LogEntry): void {
    // do nothing here
  }

  /*
   * COLUMN LOG ENTRY ID
   */

  static add_column_log_entry_id(entry: LogEntry): void {
    let { log_entry_id } = entry.params;

    assert_type_number(log_entry_id);

    log_entry_id = log_entry_id % 100;

    entry.column_log_entry_id = isNaN(log_entry_id) ? '' : `#${log_entry_id.toString().padStart(2, '0')}`;

    entry.column_log_entry_id = entry.column_log_entry_id.pad(ConstantsLogger.LOGGER_COLUMN_LOG_ENTRY_NUMBER_CHARS);

    let color = isNaN(log_entry_id) ? ConstantsLogger.COLOR_NOT_IMPORTANT : number_to_hex(log_entry_id);

    this.colorize_column_log_entry_id(color, entry);
  }

  static colorize_column_log_entry_id(color: string, entry: LogEntry): void {
    // do nothing here
  }

  /*
   * COLUMN MESSAGE
   */

  static add_column_message(entry: LogEntry) {
    entry.column_message = entry.message;

    assert_type_string(entry.params.namespace);

    let color = this.get_color(entry.params.namespace);

    if (color) this.colorize_column_message(color, entry);
  }

  static colorize_column_message(color: string, entry: LogEntry): void {
    // do nothing here
  }
}
